import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { encodeApiError, generateUUID, TApiErrorResponse } from '@sandbox-platform/shared-ui';

import { CommissionInvoiceApiService } from '../../../../services/commission-invoice-api.service';
import { CommissionPaymentApiService } from '../../../../services/commission-payment-api.service';
import {
  CANCELLABLE_INVOICE_STATUSES,
  CommissionInvoiceDTO,
  PAYABLE_INVOICE_STATUSES,
} from '../../../../types/commission-invoice.types';
import {
  CommissionPaymentDTO,
  CreateCommissionPaymentDTO,
  ReverseCommissionPaymentDTO,
} from '../../../../types/commission-payment.types';

interface CommissionInvoiceDetailState {
  invoice: CommissionInvoiceDTO | null;
  payments: CommissionPaymentDTO[];
  loading: boolean;
  paymentsLoading: boolean;
  error: string | TApiErrorResponse | null;
}

const initialState: CommissionInvoiceDetailState = {
  invoice: null,
  payments: [],
  loading: false,
  paymentsLoading: false,
  error: null,
};

export const CommissionInvoiceDetailStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    canAddPayment: computed(() => {
      const invoice = store.invoice();
      if (!invoice) return false;
      return PAYABLE_INVOICE_STATUSES.includes(invoice.status);
    }),
    canApprove: computed(() => store.invoice()?.status === 'pending_approval'),
    canCancel: computed(() => {
      const invoice = store.invoice();
      if (!invoice) return false;
      return CANCELLABLE_INVOICE_STATUSES.includes(invoice.status);
    }),
    remainingAmount: computed(() => {
      const invoice = store.invoice();
      if (!invoice) return 0;
      return invoice.total_amount_minor - invoice.paid_amount_minor;
    }),
    payer: computed(() => store.invoice()?.invoice_recipient_name ?? null),
  })),
  withMethods(
    (
      store,
      invoiceApi = inject(CommissionInvoiceApiService),
      paymentApi = inject(CommissionPaymentApiService)
    ) => ({
      async loadInvoice(id: string): Promise<void> {
        patchState(store, { loading: true, error: null });
        try {
          const response = await firstValueFrom(invoiceApi.getById(id));
          patchState(store, { invoice: response.data });
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to load invoice'),
          });
        } finally {
          patchState(store, { loading: false });
        }
      },

      async loadPayments(invoiceId: string): Promise<void> {
        patchState(store, { paymentsLoading: true });
        try {
          const response = await firstValueFrom(
            paymentApi.list(invoiceId)
          );
          patchState(store, { payments: response.data?.items ?? [] });
        } catch {
          // non-critical — silently swallow
        } finally {
          patchState(store, { paymentsLoading: false });
        }
      },

      async approve(id: string): Promise<boolean> {
        patchState(store, { loading: true, error: null });
        try {
          const response = await firstValueFrom(
            invoiceApi.approve(id, generateUUID())
          );
          patchState(store, { invoice: response.data });
          return true;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to approve invoice'),
          });
          return false;
        } finally {
          patchState(store, { loading: false });
        }
      },

      async cancel(id: string): Promise<boolean> {
        patchState(store, { loading: true, error: null });
        try {
          const response = await firstValueFrom(
            invoiceApi.cancel(id, generateUUID())
          );
          patchState(store, { invoice: response.data });
          return true;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to cancel invoice'),
          });
          return false;
        } finally {
          patchState(store, { loading: false });
        }
      },

      async addPayment(dto: CreateCommissionPaymentDTO): Promise<boolean> {
        patchState(store, { paymentsLoading: true, error: null });
        try {
          await firstValueFrom(paymentApi.create(dto, generateUUID()));
          // reload both invoice (for paid_amount_minor) and payments
          const invoiceId = store.invoice()!.id;
          const [invoiceRes, paymentsRes] = await Promise.all([
            firstValueFrom(invoiceApi.getById(invoiceId)),
            firstValueFrom(paymentApi.list(invoiceId)),
          ]);
          patchState(store, {
            invoice: invoiceRes.data,
            payments: paymentsRes.data?.items ?? [],
          });
          return true;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to record payment'),
          });
          return false;
        } finally {
          patchState(store, { paymentsLoading: false });
        }
      },

      async reversePayment(
        paymentId: string,
        dto: ReverseCommissionPaymentDTO
      ): Promise<boolean> {
        patchState(store, { paymentsLoading: true, error: null });
        try {
          await firstValueFrom(paymentApi.reverse(paymentId, dto, generateUUID()));
          const invoiceId = store.invoice()!.id;
          const [invoiceRes, paymentsRes] = await Promise.all([
            firstValueFrom(invoiceApi.getById(invoiceId)),
            firstValueFrom(paymentApi.list(invoiceId)),
          ]);
          patchState(store, {
            invoice: invoiceRes.data,
            payments: paymentsRes.data?.items ?? [],
          });
          return true;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to reverse payment'),
          });
          return false;
        } finally {
          patchState(store, { paymentsLoading: false });
        }
      },

      reset(): void {
        patchState(store, { invoice: null, payments: [], error: null });
      },

      clearError(): void {
        patchState(store, { error: null });
      },
    })
  )
);

export type CommissionInvoiceDetailStoreType = InstanceType<
  typeof CommissionInvoiceDetailStore
>;
